import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";

function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAmounts, setEditAmounts] = useState({});
  const [editTimes, setEditTimes] = useState({});

  const formatDate = (createdAt) => {
    if (!createdAt) return "-";
    try {
      if (createdAt.toDate) return createdAt.toDate().toLocaleString();
      if (createdAt.seconds)
        return new Date(createdAt.seconds * 1000).toLocaleString();
      if (typeof createdAt === "string") return createdAt;
      return "-";
    } catch {
      return "-";
    }
  };

  const isToday = (createdAt) => {
    try {
      const date =
        createdAt?.toDate?.() ||
        (createdAt?.seconds
          ? new Date(createdAt.seconds * 1000)
          : new Date(createdAt));
      const today = new Date();
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      );
    } catch {
      return false;
    }
  };

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "withdrawals"));
      const snap = await getDocs(q);

      const withdrawalsData = await Promise.all(
        snap.docs.map(async (docSnap) => {
          const w = { id: docSnap.id, ...docSnap.data() };
          const userQuery = query(
            collection(db, "users"),
            where("username", "==", w.username)
          );
          const userSnap = await getDocs(userQuery);

          let fullName = "-";
          let country = "-";
          if (!userSnap.empty) {
            const userData = userSnap.docs[0].data();
            fullName = userData.fullName || "-";
            country = userData.country || "-";
          }

          return { ...w, fullName, country };
        })
      );

      const sorted = withdrawalsData.sort((a, b) => {
        const aToday = isToday(a.createdAt);
        const bToday = isToday(b.createdAt);
        if (aToday && !bToday) return -1;
        if (!aToday && bToday) return 1;
        const aDate =
          a.createdAt?.toDate?.() ||
          (a.createdAt?.seconds
            ? new Date(a.createdAt.seconds * 1000)
            : new Date(a.createdAt));
        const bDate =
          b.createdAt?.toDate?.() ||
          (b.createdAt?.seconds
            ? new Date(b.createdAt.seconds * 1000)
            : new Date(b.createdAt));
        return bDate - aDate;
      });

      setWithdrawals(sorted);
    } catch (err) {
      console.error("Error fetching withdrawals:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const updateStatus = async (id, status) => {
    const confirmed = window.confirm(
      `Are you sure you want to mark this withdrawal as ${status}?`
    );
    if (!confirmed) return;

    try {
      const docRef = doc(db, "withdrawals", id);
      const withdrawalDoc = await getDoc(docRef);
      if (!withdrawalDoc.exists()) return;

      const withdrawal = withdrawalDoc.data();

      if (status === "rejected") {
        const userQuery = query(
          collection(db, "users"),
          where("username", "==", withdrawal.username)
        );
        const userSnap = await getDocs(userQuery);
        if (!userSnap.empty) {
          const userDoc = userSnap.docs[0];
          const userData = userDoc.data();
          const balanceField =
            withdrawal.currency === "TSH" ? "tshBalance" : "usdtBalance";
          const newBalance =
            (userData[balanceField] || 0) + withdrawal.amount;
          await updateDoc(userDoc.ref, { [balanceField]: newBalance });
        }
      }

      await updateDoc(docRef, { status });

      setWithdrawals((prev) =>
        prev.map((w) => (w.id === id ? { ...w, status } : w))
      );
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const updateAmount = async (id, newAmount) => {
    if (!newAmount || isNaN(newAmount)) return;
    try {
      const docRef = doc(db, "withdrawals", id);
      await updateDoc(docRef, { amount: Number(newAmount) });

      setWithdrawals((prev) =>
        prev.map((w) =>
          w.id === id ? { ...w, amount: Number(newAmount) } : w
        )
      );
      setEditAmounts((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    } catch (err) {
      console.error("Error updating amount:", err);
    }
  };

  // ✅ NEW: Update createdAt
  const updateTime = async (id, newTime) => {
    if (!newTime) return;
    try {
      const newTimestamp = Timestamp.fromDate(new Date(newTime));
      const docRef = doc(db, "withdrawals", id);
      await updateDoc(docRef, { createdAt: newTimestamp });

      setWithdrawals((prev) =>
        prev.map((w) =>
          w.id === id ? { ...w, createdAt: newTimestamp } : w
        )
      );

      setEditTimes((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
    } catch (err) {
      console.error("Error updating time:", err);
    }
  };

  const deleteWithdrawal = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to DELETE this withdrawal permanently?"
    );
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "withdrawals", id));
      setWithdrawals((prev) => prev.filter((w) => w.id !== id));
    } catch (err) {
      console.error("Error deleting withdrawal:", err);
    }
  };

  if (loading) return <p>Loading withdrawals...</p>;

  return (
    <div className="admin-withdrawals">
      <h2>Admin Withdrawals (Za Leo Juu Kabisa)</h2>

      {withdrawals.length === 0 ? (
        <p style={{ color: "red", fontWeight: "bold" }}>
          Hakuna withdrawals bado.
        </p>
      ) : (
        <table border="1" cellPadding="5" cellSpacing="0">
          <thead>
            <tr>
              <th>Username</th>
              <th>Full Name</th>
              <th>Country</th>
              <th>Amount</th>
              <th>Net Amount (95%)</th>
              <th>Currency</th>
              <th>Status</th>
              <th>Network</th>
              <th>Phone</th>
              <th>Address</th>
              <th>USDT Type</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {withdrawals.map((w) => {
              const netAmount = w.amount
                ? (w.amount * 0.95).toFixed(2)
                : 0;

              const dateValue = (() => {
                try {
                  const d =
                    w.createdAt?.toDate?.() ||
                    (w.createdAt?.seconds
                      ? new Date(w.createdAt.seconds * 1000)
                      : new Date(w.createdAt));
                  return d.toISOString().slice(0, 16);
                } catch {
                  return "";
                }
              })();

              return (
                <tr
                  key={w.id}
                  style={{
                    backgroundColor: isToday(w.createdAt)
                      ? "#d1ffd1"
                      : "white",
                  }}
                >
                  <td>{w.username}</td>
                  <td>{w.fullName}</td>
                  <td>{w.country}</td>
                  <td>
                    <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                      <input
                        type="number"
                        value={editAmounts[w.id] ?? w.amount}
                        onChange={(e) =>
                          setEditAmounts((prev) => ({
                            ...prev,
                            [w.id]: e.target.value,
                          }))
                        }
                        style={{ width: "100px" }}
                      />
                      <button onClick={() => updateAmount(w.id, editAmounts[w.id])}>
                        Save
                      </button>
                    </div>
                  </td>
                  <td>{netAmount}</td>
                  <td>{w.currency}</td>
                  <td>{w.status}</td>
                  <td>{w.network || "-"}</td>
                  <td>{w.phone || "-"}</td>
                  <td>{w.address || "-"}</td>
                  <td>{w.usdtType || "-"}</td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                      <input
                        type="datetime-local"
                        value={editTimes[w.id] ?? dateValue}
                        onChange={(e) =>
                          setEditTimes((prev) => ({
                            ...prev,
                            [w.id]: e.target.value,
                          }))
                        }
                      />
                      <button onClick={() => updateTime(w.id, editTimes[w.id])}>
                        Save Time
                      </button>
                      <small>{formatDate(w.createdAt)}</small>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                      <select
                        value={w.status}
                        onChange={(e) => updateStatus(w.id, e.target.value)}
                      >
                        <option value="pending">Pending</option>
                        <option value="approving">Approving</option>
                        <option value="success">Success</option>
                        <option value="rejected">Rejected</option>
                      </select>
                      <button
                        style={{
                          background: "red",
                          color: "white",
                          fontWeight: "bold",
                        }}
                        onClick={() => deleteWithdrawal(w.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default AdminWithdrawals;